<template>
	<div class="setting_ps px-7 py-4">
		<div>
			<v-row no-gutters>
				<v-col cols="12">
					<Row class="flex-align-center flex-justify-space-between">
						<h3 class="reg mt-2 mb-1">Manage Custom Domains</h3>
						<Row class="flex-align-center">
							<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId" v-if="billingFeatureId">
								<template #default="{ indicator, isUpgradeRequired }">
									<PrimaryButton
										:disabled="!hasPermissionToChangeAccount"
										class="ml-5"
										@click="(e) => addDomain(e, isUpgradeRequired, indicator)"
									>
										<Row>
											Add Domain
											<div class="pl-2" v-if="billingFeatureId && isUpgradeRequired">
												<component :is="indicator" :billingFeatureId="billingFeatureId" iconOnly />
											</div>
										</Row>
									</PrimaryButton>
								</template>
							</MaybeUpgradeRequiredIndicator>
						</Row>
					</Row>
					<v-row>
						<v-col cols="12" class="mt-3">
							<v-data-table
								:options="{
									itemsPerPage: 50,
								}"
								:footer-props="{
									'items-per-page-options': [10, 20, 30, 40, 50, 100],
								}"
								v-model="selected"
								:headers="headers"
								:items="customDomains"
								item-key="id"
								class="domains_table"
								fixed-header
								:search="search"
							>
								<template v-slot:item.is_default="{ item }">
									<div class="is-default">
										<template v-if="item.is_default">
											<v-icon color="success">mdi-check-circle</v-icon>
											<span v-if="item.verified" class="setDefaultButton" @click="setDefaultDomain(item, false)">Unset default</span>
										</template>
										<template v-else>
											<v-icon color="error">mdi-alert-circle</v-icon>
											<span v-if="item.verified" class="setDefaultButton" @click="setDefaultDomain(item, true)">Set as default</span>
										</template>
									</div>
								</template>
								<template v-slot:item.verified="{ item }">
									<template v-if="item.verified">
										<v-icon color="success">mdi-check-circle</v-icon>
										Configuration verified
									</template>
									<template v-else>
										<v-icon color="orange">mdi-alert-circle</v-icon>
										Not verified.
										<span class="text-underline cursor-pointer" @click="openDomainDialog(item)">See Instructions</span>
									</template>
								</template>
								<template v-slot:item.verify="{ item }">
									<div v-if="!item.verified">
										<Row v-if="!!loadingState[item.id]" class="width-100">
											<AppLoader />
										</Row>
										<h3 @click="verifyDomain(item)" class="link purple-text" v-else>Verify</h3>
									</div>
									<span v-else> - </span>
								</template>
								<template v-slot:item.actions="{ item }">
									<AppVMenu width="200" min-width="200" bottom left offset-y content-class="dropdown-menu-container">
										<template v-slot:activator="{ on }">
											<AnemicButton
												v-on="on"
												@click="(e) => e.preventDefault()"
												width="22"
												height="22"
												icon
												fab
												x-small
												class="flex-centerize menu-button ml-12"
											>
												<SIcon name="three-dot-menu" width="13" class="menu-icon" />
											</AnemicButton>
										</template>
										<v-list>
											<v-list-item class="item-container" @click="deleteDomain(item)">
												<v-list-item-title>
													<span style="color: #3e495a; font-size: 14px; font-weight: 400"> Delete domain </span>
												</v-list-item-title>
											</v-list-item>
										</v-list>
									</AppVMenu>
								</template>
							</v-data-table>
						</v-col>
					</v-row>
				</v-col>
			</v-row>
		</div>
	</div>
</template>

<script>
import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const';
import { hasAccountPermission, isAccountOwner } from '@/store/modules/account/getters';
import { fetchCustomDomains, setDefaultCustomDomain, verifyCustomDomain, deleteCustomDomain } from '@/store/modules/customDomains/actions';
import { useCustomDomainsArray } from '@/store/modules/customDomains/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { ADMIN_ROLE } from '@/utils/account-service.const';
export default {
	name: 'SettingsManageCustomDomains',
	components: { MaybeUpgradeRequiredIndicator },
	props: {},
	computed: {
		...mapGettersShorthand({ isAccountOwner, hasAccountPermission, useCustomDomainsArray }),
		billingFeatureId() {
			return FeatureTypes.CustomerCustomDomains;
		},
		tableHeight() {
			return this.$vuetify.breakpoint.height - 450;
		},
		customDomains() {
			return this.useCustomDomainsArray;
		},

		isCurrentUserAdminOfWorkspace() {
			return this.user.profile.account.role === ADMIN_ROLE;
		},
		hasPermissionToChangeAccount() {
			return this.hasAccountPermission(AccountPermissions.ChangeAccount);
		},
		headers() {
			return [
				{ text: 'Domain', value: 'domain', align: 'left', show: true },
				{ text: 'Status', value: 'verified', align: 'left', show: true },
				{ text: 'Verify', value: 'verify', align: 'left', show: this.hasPermissionToChangeAccount },
				{ text: 'Actions', value: 'actions', align: 'center', sortable: false, width: '150px', show: this.hasPermissionToChangeAccount },
			].filter((header) => header.show);
		},
	},
	watch: {
		account: {
			handler() {
				this.fetchCustomDomains();
			},
			deep: true,
		},
	},
	methods: {
		...mapActionsShorthand({ fetchCustomDomains, setDefaultCustomDomain, verifyCustomDomain, deleteCustomDomain }),
		addDomain(event, isUpgradeRequired, indicator) {
			if (isUpgradeRequired && indicator?.setup) {
				indicator.setup().openUpgrade(event, this.billingFeatureId);
			} else {
				AppDialogManagerService.openNewCustomDomainDialog();
			}
		},
		openDomainDialog(domain) {
			AppDialogManagerService.openNewCustomDomainDialog({ currentDomain: domain });
		},
		setDefaultDomain(domain, is_default = true) {
			this.setDefaultCustomDomain({ domainId: domain.id, is_default });
		},
		async verifyDomain(domain) {
			try {
				this.$set(this.loadingState, domain.id, true);
				const verifyResult = await this.verifyCustomDomain({ domainId: domain.id });
				if (verifyResult.verified) {
					this.$toast.success('Domain verified successfully');
				} else {
					this.$toast.error('Domain verification failed. it might take up to 24 hours to propagate');
				}
			} catch (err) {
				console.error(err);
				this.$toast.error('Something went wrong, please try again later');
			} finally {
				this.$set(this.loadingState, domain.id, false);
			}
		},
		async deleteDomain(domain) {
			const confirmedDelete = await AppDialogManagerService.openConfirmationDialog({
				title: `Delete custom domain ${domain.domain}?`,
				description: `Are you sure you want to delete this custom domain? this action cannot be undone.`,
				yesText: 'Yes',
				noText: 'No',
				isDanger: true,
			});
			if (confirmedDelete === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				try {
					await this.deleteCustomDomain({ domainId: domain.id });
				} catch {
					this.$toast.error('Something went wrong, please try again later');
				}
			}
		},
	},
	mounted() {
		this.fetchCustomDomains();
	},
	data() {
		return {
			loadingState: {},
			selected: [],
			search: '',
		};
	},
};
</script>

<style scoped lang="scss">
.dropdown-menu-container {
	width: 300px;
	overflow: hidden;
	border-radius: 11px;
	background: var(--bg-color);
	box-shadow: 0px 3px 16px #0000004d;
}
.bulkActionsMenu {
	display: flex;
	align-items: center;
}
h4.reg {
	font-weight: 400 !important;
}
th {
	border-bottom: 1px solid #93a1bb !important;
	color: #354052 !important;
	font-weight: 600 !important;
	font-size: 12px !important;
	padding-bottom: 0px !important;
	line-height: 20px !important;
	height: 36px !important;
}
td {
	border-bottom: 1px solid #eff1f7 !important;
	.owner {
		font-size: 11px;
		opacity: 0.5;
	}
}
.menu_btn {
	border: 1px solid #959da1 !important;
	width: 25px;
	height: 25px;
	background: #fafcff;
	transition: all 0.2s;
	opacity: 0;
}
.menu_btn.show {
	opacity: 1;
}
.men_card {
	width: 210px;
	z-index: 5;
	border-radius: 2px !important;
	border-bottom-right-radius: 6px !important;
}
.bulk-action-button {
	height: 35px !important;
	font-size: 14px !important;
	color: #6c6adc !important;
	border-radius: 6px !important;
	letter-spacing: 0px !important;
	font-weight: normal !important;
	background: var(--white) !important;
	padding: 0px 2px 0px 10px !important;
	border: 1px solid #6c6adc !important;
	&.disabled {
		border: none !important;
	}
}
.chevron {
	margin-top: 2px;
	margin-left: 6px;
	margin-right: 0px;
	transition: 0.2s;
	font-size: 20px !important;
	&.rotate {
		transition: 0.2s;
		transform: rotate(180deg);
	}
}
.search-field-input {
	&::v-deep {
		&.v-text-field--enclosed .v-input__append-inner {
			margin-top: 6px !important;
		}
		&.v-text-field--outlined .v-label {
			top: 8px !important;
		}
	}
}
.domains_table {
	::v-deep {
		.v-input--selection-controls__ripple {
			height: 24px;
			width: 24px;
			left: -13px;
			top: calc(50% - 19px);
		}
		.v-icon.v-icon {
			font-size: 14px !important;
		}
		.v-input--selection-controls__input {
			width: 14px;
			height: 14px;
			border-radius: 2px;
		}
		.theme--light.v-icon {
			color: #6c6adc;
		}
		.v-icon--disabled {
			background: #e3e3e3;
		}
		.theme--light.v-icon.v-icon.v-icon--disabled {
			color: #e3e3e3 !important;
		}
	}
}
.menu-item {
	font-size: 14px;
	color: #262626;
}
.description-text {
	color: #26262680;
}
::v-deep {
	.v-data-table__wrapper {
		overflow: auto !important;
		max-height: 100% !important;
	}
}
.is-default {
	.setDefaultButton {
		display: none;
		text-decoration: underline;
		cursor: pointer;
	}
	&:hover {
		.setDefaultButton {
			display: block;
		}
	}
}
.purple-text {
	color: #6060f7;
}
.menu-button {
	background: white !important;
	&:hover {
		border: 1px solid var(--forth-border-color);
	}
	.menu-icon {
		margin-top: -6px;
		margin-left: -2px;
	}
}
.item-container {
	max-height: 30px !important;
	min-height: 30px !important;
}
</style>
